import { cn } from "@/lib/utils";
import type { InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes } from "react";

type BaseProps = {
  label: string;
  name: string;
  error?: string;
  required?: boolean;
  className?: string;
};

const fieldClasses =
  "focus-ring w-full rounded-lg border border-border bg-navy-secondary px-3.5 py-2.5 text-sm text-text-primary placeholder:text-text-muted transition focus:border-cyan/60";

function ErrorText({ error, id }: { error?: string; id: string }) {
  if (!error) return null;
  return (
    <p id={id} role="alert" className="mt-1.5 text-xs text-status-error">
      {error}
    </p>
  );
}

export function TextField({
  label,
  name,
  error,
  required,
  className,
  ...rest
}: BaseProps & InputHTMLAttributes<HTMLInputElement>) {
  const errorId = `${name}-error`;
  return (
    <div className={className}>
      <label htmlFor={name} className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-text-muted">
        {label} {required && <span className="text-cyan">*</span>}
      </label>
      <input
        id={name}
        name={name}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        className={cn(fieldClasses, error && "border-status-error/60")}
        {...rest}
      />
      <ErrorText error={error} id={errorId} />
    </div>
  );
}

export function TextAreaField({
  label,
  name,
  error,
  required,
  className,
  ...rest
}: BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const errorId = `${name}-error`;
  return (
    <div className={className}>
      <label htmlFor={name} className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-text-muted">
        {label} {required && <span className="text-cyan">*</span>}
      </label>
      <textarea
        id={name}
        name={name}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        className={cn(fieldClasses, "min-h-[110px] resize-y", error && "border-status-error/60")}
        {...rest}
      />
      <ErrorText error={error} id={errorId} />
    </div>
  );
}

export function SelectField({
  label,
  name,
  error,
  required,
  className,
  children,
  ...rest
}: BaseProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const errorId = `${name}-error`;
  return (
    <div className={className}>
      <label htmlFor={name} className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-text-muted">
        {label} {required && <span className="text-cyan">*</span>}
      </label>
      <select
        id={name}
        name={name}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        className={cn(fieldClasses, error && "border-status-error/60")}
        {...rest}
      >
        {children}
      </select>
      <ErrorText error={error} id={errorId} />
    </div>
  );
}
